import BaseButton from '@/components/ui/BaseButton';
import { useRegistrationState } from '@/provider/registration/registrationContext';
import { Box, TablePagination } from '@mui/material';
import { useTranslation } from 'react-i18next';

type RegisterFooterAction = {
    label: string;
    disabled?: boolean;
} | null;

type RegisterDeviceFooterProps = {
    count: number;
    page: number;
    rowsPerPage: number;
    action: RegisterFooterAction;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    onActionClick: () => void;
};

const RegisterDeviceFooter = ({
    count,
    page,
    rowsPerPage,
    action,
    onPageChange,
    onRowsPerPageChange,
    onActionClick,
}: RegisterDeviceFooterProps) => {
    const { t } = useTranslation('common');
    const { state } = useRegistrationState();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flexShrink: 0,
                bgcolor: '#FFFFFF',
                borderTop: '1px solid #E5E7EB',
                pb: 'env(safe-area-inset-bottom)',
            }}
        >
            <TablePagination
                component="div"
                count={count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, nextPage) => {
                    onPageChange(nextPage);
                }}
                onRowsPerPageChange={(event) => {
                    onRowsPerPageChange(Number(event.target.value));
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={t('Rows per page')}
                labelDisplayedRows={({ from, to, count }) => {
                    return `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`;
                }}
                sx={{
                    width: '100%',
                    overflow: 'hidden',

                    '.MuiTablePagination-toolbar': {
                        px: 2,
                        minHeight: 40,
                        display: 'flex',
                        alignItems: 'center',
                    },

                    '.MuiTablePagination-spacer': {
                        display: 'none',
                    },

                    '.MuiTablePagination-selectLabel': {
                        fontSize: 11,
                        color: '#64748B',
                        m: 0,
                    },

                    '.MuiTablePagination-input': {
                        mr: 0.5,
                        ml: 0.5,
                    },

                    '.MuiTablePagination-displayedRows': {
                        fontSize: 12,
                        color: '#64748B',
                        m: 0,
                        ml: 'auto',
                    },

                    '.MuiTablePagination-actions': {
                        ml: 0,
                    },
                }}
            />

            {action && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        borderTop: '1px solid #F1F5F9',
                        p: 1,
                    }}
                >
                    <BaseButton
                        fullWidth
                        tabIndex={-1}
                        variant="contained"
                        color="primary"
                        loading={state.deviceInfoState.doingTask}
                        disabled={state.deviceInfoState.doingTask || action.disabled}
                        onClick={onActionClick}
                        sx={{
                            height: 36,
                            borderRadius: 3,
                            fontWeight: 800,
                            textTransform: 'none',
                            maxWidth: '85vw',
                        }}
                    >
                        {t(action.label)}
                    </BaseButton>
                </Box>
            )}
        </Box>
    );
};

export default RegisterDeviceFooter;
